import React, { useState, useEffect } from "react";
import {
  Grid,
  useTheme,
  Typography,
  Box,
  Menu,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  AlertTitle,
  IconButton,
  CircularProgress,
  Fade,
  Button,
  Fab,
  styled,
  keyframes,
  useMediaQuery,
  Snackbar,
} from "@mui/material";
import {
  Refresh,
  Tune,
  AddCircleOutline,
  Edit,
  Delete,
} from "@mui/icons-material";
import SearchBar from "../components/SearchBar";
import FilterModal from "../components/FilterModal";
import BackWithBreadcrumbAppbar from "../components/BackWithBreadcrumbAppbar";
import { useQuery, gql, useMutation } from "@apollo/client";
import LoadingPage from "./LoadingPage";
import { useTranslation } from "react-i18next";
import FilterButton from "../components/FilterButton";
import { UPLOAD_IMAGES_API_URL } from "../constant";
import useUserData from "../hooks/useUserData";
import FilteredExhibitors from "../components/FilteredExhibitors";
import SponsorForm from "../components/SponsorForm";
import FilteredSponsors from "../components/FilteredSponsors";

const GET_ALL_SPONSORS = gql`
  query GetAllSponsors($roleType: String) {
    getAllSponsors(roleType: $roleType) {
      id
      company_name
      email
      country
      stallNo
      website
      sponsorship_level
      profile_pic
      company_profile
      team_members
      isConnected
    }
  }
`;
const UPDATE_SPONSOR = gql`
  mutation UpdateSponsor($id: ID!, $updateData: String!) {
    updateSponsor(id: $id, updateData: $updateData) {
      id
      success
      message
    }
  }
`;
const ADD_SPONSOR = gql`
  mutation AddSponsor($jsonData: String!) {
    addSponsor(jsonData: $jsonData) {
      id
      success
      message
    }
  }
`;
const DELETE_SPONSOR = gql`
  mutation DeleteSponsor($id: ID!) {
    deleteSponsor(id: $id) {
      id
      success
      message
    }
  }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const ShinyFab = styled(Fab)(({ theme }) => ({
  position: "fixed",
  bottom: theme.spacing(4),
  right: theme.spacing(4),
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  animation: `${shimmer} 50s infinite linear`,
  backgroundSize: "15% auto",
  transition: "transform 0.3s, box-shadow 0.3s",
  backdropFilter: "blur(10px)",
  "&:hover": {
    transform: "scale(1.1)",
    boxShadow: theme.shadows[10],
  },
}));

const SponsorList = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState("");
  const [sponsors, setSponsors] = useState([]);
  const [filteredSponsors, setFilteredSponsors] = useState([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const { data: user_data, refetch: userRefetch } = useUserData();
  let userdata = user_data?.me;
  if (typeof user_data?.me === "string") {
    userdata = JSON.parse(user_data?.me);
  }
  // console.log(filteredSponsors);
  const user = userdata;
  const [filters, setFilters] = useState({
    role: [],
    company: [],
    designation: [],
    country: [],
  });

  const [formOpen, setFormOpen] = useState(false);
  const [editFormOpen, setEditFormOpen] = useState(false);
  const [newProduct, setNewProduct] = useState({
    company_name: "",
    company_profile: "",
    profile_pic: null,
  });
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [editMode, setEditMode] = useState(false);
  const [selectedSponsor, setSelectedSponsor] = useState(null);

  const [updateSponsor] = useMutation(UPDATE_SPONSOR);
  const [deleteSponsor] = useMutation(DELETE_SPONSOR);

  // Handle delete sponsor
  const handleDelete = async (sponsorId) => {
    if (window.confirm(t("areYouSureToDelete"))) {
      try {
        const { data } = await deleteSponsor({ variables: { id: sponsorId } });
        if (data?.deleteSponsor?.success) {
          setSnackbar({
            open: true,
            message: t("sponsorDeleted"),
            severity: "success",
          });
          refetch();
        } else {
          setSnackbar({
            open: true,
            message: t("deleteError"),
            severity: "error",
          });
        }
      } catch (error) {
        setSnackbar({ open: true, message: error.message, severity: "error" });
      }
    }
  };

  // Handle edit sponsor
  const handleEditClick = (sponsor) => {
    // console.log("sponsor", sponsor);
    setSelectedSponsor(sponsor);
    setNewProduct(sponsor);
    setEditMode(true);
    setEditFormOpen(true);
  };
  const [addSponsor, { loading: uploadLoading, error: uploadError }] =
    useMutation(ADD_SPONSOR);
  // const handleAddProduct = async (formData) => {
  //   await addSponsor({
  //     variables: {
  //       jsonData: JSON.stringify(formData)
  //     }
  //   }).then(async (res) => {
  //     if (res?.data?.addSponsor?.success) {
  //       const sponsor_id = res?.data?.addSponsor?.id;
  //       const imageFile = formData.profile_pic;
  //       const formDataImage = new FormData();
  //       formDataImage.append("image", imageFile);

  //       const imageResponse = await fetch(`${UPLOAD_IMAGES_API_URL}/upload/user/${user.eventId}/sponsors/${sponsor_id}`, {
  //         method: 'POST',
  //         body: formDataImage, // Use FormData here
  //       });

  //       const imageData = await imageResponse.json();
  //       console.log("imageData",imageData);
  //       if (imageData.success) {
  //         setSuccessMessage(t('sponsorAddedSuccessfully'));
  //         setTimeout(() => {
  //           setSuccessMessage('');
  //           setFormOpen(false);
  //           refetch();
  //         }, 2000);
  //       } else {
  //         setErrorMessage(t('errorUploadingImage'));
  //         setTimeout(() => {
  //           setErrorMessage('');
  //         }, 2000);
  //       }
  //     } else {
  //       setErrorMessage(t('errorAddingSponsor'));
  //       setTimeout(() => {
  //         setErrorMessage('');
  //       }, 2000);

  //     }

  //   }).catch(() => {
  //     setErrorMessage(t('errorAddingSponsor'));
  //     setTimeout(() => {
  //       setErrorMessage('');
  //     }, 2000);
  //   }).finally(() => {
  //     setUploading(false);
  //   });

  // };

  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const roleType = "sponsors";

  const { loading, error, data, refetch } = useQuery(GET_ALL_SPONSORS, {
    variables: { roleType },
    notifyOnNetworkStatusChange: true,
    skip: !roleType,
    fetchPolicy: "cache-and-network",
  });

  const handleSubmit = async (formData) => {
    setUploading(true);
    try {
      // Add new sponsor
      const { data: addData } = await addSponsor({
        variables: { jsonData: JSON.stringify(formData) },
      });

      if (addData?.addSponsor?.success) {
        const sponsor_id = addData.addSponsor.id;
        const imageFile = formData.profile_pic;

        if (imageFile instanceof File) {
          const formDataImage = new FormData();
          formDataImage.append("image", imageFile);
          const imageResponse = await fetch(
            `${UPLOAD_IMAGES_API_URL}/upload/user/${user.eventId}/sponsors/${sponsor_id}`,
            { method: "POST", body: formDataImage }
          );

          const imageData = await imageResponse.json();
          if (!imageData.success) {
            throw new Error(t("errorUploadingImage"));
          }
        }

        setSnackbar({
          open: true,
          message: t("sponsorAddedSuccessfully"),
          severity: "success",
        });
        refetch();
        setFormOpen(false);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error.message ||
          t(editMode ? "errorUpdatingSponsor" : "errorAddingSponsor"),
        severity: "error",
      });
    } finally {
      setUploading(false);
    }
  };
  const handleUpdate = async (formData) => {
    setUploading(true);
    try {
      // Update existing sponsor
      const { data: updateData } = await updateSponsor({
        variables: {
          id: selectedSponsor.id,
          updateData: JSON.stringify(formData),
        },
      });

      if (updateData?.updateSponsor?.success) {
        // Handle image upload if changed
        if (formData.profile_pic instanceof File) {
          const formDataImage = new FormData();
          formDataImage.append("image", formData.profile_pic);
          const imageResponse = await fetch(
            `${UPLOAD_IMAGES_API_URL}/upload/user/${user.eventId}/sponsors/${selectedSponsor.id}`,
            { method: "POST", body: formDataImage }
          );

          const imageData = await imageResponse.json();
          if (!imageData.success) {
            throw new Error(t("errorUploadingImage"));
          }
        }

        setSnackbar({
          open: true,
          message: t("sponsorUpdated"),
          severity: "success",
        });
        refetch();
        setEditFormOpen(false);
        setEditMode(false);
        setSelectedSponsor(null);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error.message ||
          t(editMode ? "errorUpdatingSponsor" : "errorAddingSponsor"),
        severity: "error",
      });
    } finally {
      setUploading(false);
    }
  };

  const renderActions = (sponsor) => {
    if (user?.email === "mohd.yahiya7@gmail.com") {
      return (
        <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
          <IconButton
            onClick={() => handleEditClick(sponsor)}
            size="small"
            sx={{ color: theme.palette.primary.main }}
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            onClick={() => handleDelete(sponsor.id)}
            size="small"
            sx={{ color: theme.palette.error.main }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      );
    }
    return null;
  };

  useEffect(() => {
    if (data?.getAllSponsors) {
      // Create sorted copy while preserving original data
      const filteredData = [...(data.getAllSponsors || [])].sort((a, b) =>
        (a.company_name || "").localeCompare(b.company_name || "")
      );

      // console.log("Sorted sponsors:", filteredData);

      // Update state with sorted data
      setSponsors(filteredData);
      setFilteredSponsors(filteredData);
    } else {
      // Clear state if no data
      setSponsors([]);
      setFilteredSponsors([]);
    }
  }, [data]);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [sortOption, setSortOption] = useState("");

  useEffect(() => {
    let filtered = [...sponsors];

    // Apply search first
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter((exhibitor) =>
        exhibitor.company_name.toLowerCase().includes(searchLower)
      );
    }
    if (filters.company.length > 0) {
      filtered = filtered.filter((a) =>
        filters.company?.includes(a?.company_name)
      );
    }

    if (filters.country.length > 0) {
      filtered = filtered.filter((a) => filters.country.includes(a.country));
    }

    // Apply sorting
    if (sortOption) {
      filtered.sort((a, b) => {
        let field;
        if (sortOption === "name") {
          field = "company_name";
        } else {
          field = "country";
        }
        return a[field].localeCompare(b[field]);
      });
    }

    setFilteredSponsors(filtered);
    setCurrentPage(1);
  }, [sponsors, searchQuery, filters, sortOption]);

  const handleFilterApply = (newFilters) => {
    setFilters(newFilters);
    setFilterModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchQuery(event);
  };

  const handleFilter = () => {
    setFilterModalOpen(true);
  };

  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortSelect = (option) => {
    setSortOption(option);
    setSortAnchorEl(null);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSponsors = filteredSponsors?.slice(startIndex, endIndex);
  const handleRetry = () => {
    refetch();
  };

  if (error) {
    return (
      <>
        <BackWithBreadcrumbAppbar
          links={[{ label: t("dashboard"), href: "/" }]}
          currentPage={t("sponsors")}
        />
        <Box sx={{ p: 3, height: "100vh" }}>
          <Alert
            severity="error"
            sx={{
              mt: 4,
              borderRadius: 2,
              boxShadow: theme.shadows[2],
            }}
            action={
              <IconButton color="inherit" onClick={handleRetry}>
                <Refresh />
              </IconButton>
            }
          >
            <AlertTitle>{t("Error")}</AlertTitle>
            {t("404")} ({error?.message || "Something went wrong"})
          </Alert>
        </Box>
      </>
    );
  }

  return (
    <>
      <BackWithBreadcrumbAppbar
        links={[{ label: t("dashboard"), href: "/" }]}
        currentPage={t("sponsors")}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box
        sx={{
          padding: theme.spacing(1),
          position: "relative",
          maxWidth: 1600,
          margin: "0 auto",
        }}
      >
        <Fade in={!loading} timeout={300}>
          <Box>
            <Typography
              variant="h3"
              align="center"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 700,
                my: 3,
                textShadow: `0 2px 4px ${theme.palette.action.hover}`,
              }}
            >
              {t("sponsorsList")}
            </Typography>

            <Grid
              container
              spacing={1.5}
              alignItems="center"
              sx={{
                position: "sticky",
                top: isMobile ? 100 : 130,
                zIndex: 10,
                backgroundColor: theme.palette.background.paper,
                py: 2,
                px: 1,
                boxShadow: `0 2px 8px ${
                  theme.palette.mode === "dark"
                    ? "rgba(0,0,0,0.3)"
                    : "rgba(0,0,0,0.1)"
                }`,
                borderBottom: `1px solid ${theme.palette.divider}`,
                marginBottom: 3,
                transition: "all 0.2s ease",
              }}
            >
              {/* Search Bar */}
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  position: "relative",
                }}
              >
                <SearchBar
                  role="sponsor"
                  onSearch={handleSearch}
                  attendees={sponsors}
                  placeholder={t("searchSponsors")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      backgroundColor: theme.palette.background.default,
                      border: `1px solid ${theme.palette.divider}`,
                      transition: theme.transitions.create([
                        "border-color",
                        "box-shadow",
                      ]),
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                      },
                      "&.Mui-focused": {
                        boxShadow: `${theme.shadows[2]}`,
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Grid>

              {/* Filter & Sort Controls */}
              <Grid
                item
                xs={8}
                md={4}
                sx={{
                  display: "flex",
                  gap: 1,
                  justifyContent: "center",
                }}
              >
                {/* Filter Button */}
                <Box sx={{ flex: 1 }}>
                  <FilterButton
                    onFilter={handleFilter}
                    badgeContent={Object.values(filters).flat().length}
                    sx={{
                      width: "100%",
                      borderRadius: 3,
                      py: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  />
                </Box>

                {/* Sort Button */}
                <Box sx={{ flex: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={handleSortClick}
                    startIcon={<Tune fontSize="small" />}
                    sx={{
                      width: "100%",
                      borderRadius: 3,
                      py: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    {t("sort")}
                  </Button>
                  <Menu
                    anchorEl={sortAnchorEl}
                    open={Boolean(sortAnchorEl)}
                    onClose={handleSortClose}
                    PaperProps={{
                      sx: {
                        borderRadius: 2,
                        boxShadow: theme.shadows[3],
                        minWidth: 150,
                        mt: 1,
                        border: `1px solid ${theme.palette.divider}`,
                      },
                    }}
                  >
                    {["name", "country"].map((option) => (
                      <MenuItem
                        key={option}
                        onClick={() => handleSortSelect(option)}
                        selected={sortOption === option}
                        sx={{
                          "&.Mui-selected": {
                            backgroundColor: theme.palette.action.selected,
                            fontWeight: 500,
                          },
                          py: 1,
                          fontSize: "0.9rem",
                        }}
                      >
                        {t(option)}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>
              </Grid>

              {/* Items Per Page Selector */}
              <Grid
                item
                xs={4}
                md={4}
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", md: "flex-end" },
                }}
              >
                <FormControl
                  variant="outlined"
                  sx={{
                    minWidth: { xs: 80, sm: 150 },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      backgroundColor: theme.palette.background.default,
                      border: `1px solid ${theme.palette.divider}`,
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  <InputLabel shrink>{t("itemsPerPage")}</InputLabel>
                  <Select
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    label={t("itemsPerPage")}
                    sx={{
                      "& .MuiSelect-select": {
                        py: 1.1,
                        pl: 2,
                      },
                    }}
                  >
                    {[10, 25, 50, 100].map((value) => (
                      <MenuItem
                        key={value}
                        value={value}
                        sx={{
                          borderRadius: 1,
                          m: 0.5,
                          "&.Mui-selected": {
                            backgroundColor: `${theme.palette.action.selected} !important`,
                          },
                        }}
                      >
                        {value}
                      </MenuItem>
                    ))}
                    <MenuItem
                      value={sponsors.length}
                      sx={{
                        borderRadius: 1,
                        m: 0.5,
                        borderTop: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      {t("all")}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <FilteredSponsors
              role="sponsors"
              refresh={refetch}
              filteredExhibitors={filteredSponsors}
              paginatedExhibitors={paginatedSponsors}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              handlePageChange={handlePageChange}
              setFilteredSponsors={setFilteredSponsors}
              renderActions={renderActions} // Pass render actions function
            />

            <FilterModal
              open={filterModalOpen}
              onClose={() => setFilterModalOpen(false)}
              onApply={handleFilterApply}
              filters={filters}
              attendees={sponsors}
              roleName="sponsors"
            />
          </Box>
        </Fade>

        {loading && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.8)",
              zIndex: theme.zIndex.modal,
            }}
          >
            <CircularProgress
              size={80}
              thickness={2}
              sx={{
                color: theme.palette.primary.main,
                animationDuration: "800ms",
              }}
            />
          </Box>
        )}
      </Box>
      {user?.email === "mohd.yahiya7@gmail.com" && (
        <ShinyFab color="primary" onClick={() => setFormOpen(true)}>
          <AddCircleOutline />
        </ShinyFab>
      )}

      <SponsorForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditMode(false);
          setSelectedSponsor(null);
        }}
        onSubmit={handleSubmit}
        sponsor={newProduct}
        setProduct={setNewProduct}
        uploading={uploading}
        errorMessage={errorMessage}
        successMessage={successMessage}
        user={user}
        uploadLoading={uploadLoading}
        uploadError={uploadError}
        mode={"create"}
      />
      <SponsorForm
        open={editFormOpen}
        onClose={() => {
          setEditFormOpen(false);
          setEditMode(false);
          setSelectedSponsor(null);
        }}
        onSubmit={handleUpdate}
        sponsor={newProduct}
        setProduct={setNewProduct}
        uploading={uploading}
        errorMessage={errorMessage}
        successMessage={successMessage}
        user={user}
        uploadLoading={uploadLoading}
        uploadError={uploadError}
        mode={"edit"}
      />
    </>
  );
};

export default SponsorList;

// import React, { useState, useEffect } from 'react';
// import { Container, Box, Typography, Grid, Link } from '@mui/material';
// import { useTheme } from '@mui/material/styles';
// import { useTranslation } from 'react-i18next';
// import BackgroundShape from '../Assets/Background-Shape.png';
// import useUserData from '../hooks/useUserData';
// import LoadingPage from './LoadingPage';
// import SubHeader from '../components/SubHeader';
// import LogoSection from '../components/LogoSection';
// import SponsorSection from '../components/SponsorSection';
// import BackWithBreadcrumbAppbar from '../components/BackWithBreadcrumbAppbar';

// const SponsorList = () => {
//   const theme = useTheme();
//   const mode = theme.palette.mode;
//   const { t } = useTranslation();
//   const { data } = useUserData();
//   const [loading, setLoading] = useState(true);
//   let me;
//   if (typeof (data?.me) === "string") {
//     me = JSON.parse(data?.me);
//   } else {
//     me = data?.me;
//   }

//   useEffect(() => {
//     const timer = setTimeout(() => {
//       setLoading(false);
//     }, 2000); // 2 seconds

//     return () => clearTimeout(timer);
//   }, []);

//   if (loading) {
//     return <LoadingPage />
//   }

//   return (
// <>
// <BackWithBreadcrumbAppbar
//         links={[{ label: t('dashboard'), href: '/' }]}
//         currentPage={t('sponsors')}
//       />
//     <Box
//       sx={{
//         minHeight: '100vh',
//         display: 'flex',
//         flexDirection: 'column',
//         background: mode === 'light' ? '#2a4b50' : 'inherit',
//         backgroundImage: mode === 'light' ? `url(${BackgroundShape})` : 'none',
//         backgroundBlendMode: 'multiply',
//         backgroundRepeat: 'no-repeat',
//         backgroundSize: 'cover',
//         color: '#fff',
//         // padding: { xs: 2, sm: 3 },

//       }}
//     >
//       <Container
//         // maxWidth="sm"
//         sx={{
//           py: { xs: 2, sm: 2 },
//           px: { xs: 2, sm: 3 },
//           // display: 'flex',
//           // flexDirection: 'column',
//           // minHeight: '100vh',
//           // mx: 'auto',
//           // width: '100%'
//         }}
//       >
//         {/* Header Section */}

//         <SubHeader />
//         {/* Logo Section */}

//         <LogoSection />

//         {/* Sponsors & Partners Button */}

//         {/* Footer Section */}
//         <SponsorSection />
//       </Container>
//     </Box>
//     </>
//   );
// };

// export default SponsorList;
